import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Collapse } from 'src/components/transitions/Collapse';
import {
  PosConfiguration,
  PosConfigurationItem,
} from 'src/models/pos-configuration/_types';
import { SelectFilter } from 'src/shared/Filters/SelectFilter';
import toast from 'src/utils/toast';

import {
  PosConfigItemSelectorDialog,
  PosSelectedItem,
} from '../PosConfig/PosConfigItemSelectorDialog';

import ConfirmationDialog from './ConfirmationDialog';
import PosAddEditTemplateDialog from './PosAddEditTemplateDialog';
import { usePosPreviewConfig } from './PosPreviewConfigContext';
import { getIcon } from './utils';

export default function PosPreviewSideMenu() {
  const [expandedPage, setExpandedPage] = useState<number[]>([0]);
  const [hoveredPage, setHoveredPage] = useState<number | null>(null);
  const [hoveredTile, setHoveredTile] = useState<{
    pageIndex: number;
    tileIndex: number;
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editTemplateDialogOpen, setEditTemplateDialogOpen] = useState(false);
  const [
    deleteTemplateConfirmationDialogOpen,
    setDeleteTemplateConfirmationDialogOpen,
  ] = useState(false);
  const [confirmTemplateChangeDialogOpen, setConfirmTemplateChangeDialogOpen] =
    useState(false);
  const [pendingTemplateConfig, setPendingTemplateConfig] =
    useState<PosConfiguration | null>(null);
  const [confirmNewTemplateDialogOpen, setConfirmNewTemplateDialogOpen] =
    useState(false);
  const [
    confirmDuplicateTemplateDialogOpen,
    setConfirmDuplicateTemplateDialogOpen,
  ] = useState(false);
  const { t } = useTranslation('translation', {
    keyPrefix: 'posPreview',
  });
  const {
    posConfig,
    posConfigs,
    setPosConfig,
    posType,
    getTemplates,
    handleSaveConfig,
    handleCreateConfig,
    handleDeleteConfig,
    setSelectedPage,
    handleDuplicateConfig,
    selectedLocations,
    locations,
    isLoadingLocations,
    handleToggleLocation,
  } = usePosPreviewConfig();

  const handleAddTilePage = () => {
    if (!posConfig) return;

    let newConfig: PosConfigurationItem[][] = [];

    if (posConfig.config.length === 0) {
      newConfig.push([]);
    } else {
      newConfig = [...posConfig.config, []];
    }

    setPosConfig({
      ...posConfig!,
      config: newConfig,
    });

    const newPageIndex = newConfig.length - 1;
    setSelectedPage(newPageIndex);

    // Ensure the new page is expanded
    if (!expandedPage.includes(newPageIndex)) {
      setExpandedPage([...expandedPage, newPageIndex]);
    }
  };

  const handleAddTiles = (items: PosSelectedItem[]) => {
    if (!posConfig) return;

    const currentTiles = new Map<string, PosConfigurationItem>();
    posConfig.config[currentPageIndex].forEach((tile) => {
      currentTiles.set((tile.itemId || tile.itemType || '').toString(), tile);
    });

    let shouldNotifyDuplicate = false;
    let nextPosition = Array.from(currentTiles.values()).length;

    items.forEach((item) => {
      if (!item.type) {
        return;
      }

      const type = Number(item.id) ? 'item' : 'collection';
      let filterParams = '';
      if (type === 'collection') {
        const lowerCaseName = item.id.toString().toLocaleLowerCase();
        const isAll = lowerCaseName.startsWith('all');
        filterParams = isAll ? '' : item.id.toString();
      }

      if (Array.from(currentTiles.values()).length === 0) {
        currentTiles.set(item.id!.toString(), {
          filterParams,
          name: item.name,
          type,
          itemType: item.type,
          itemId: item.id.toString(),
          position: 0,
        });
      } else if (!currentTiles.has(item.id!.toString())) {
        currentTiles.set(item.id!.toString(), {
          filterParams,
          name: item.name,
          type,
          itemType: item.type,
          itemId: item.id.toString(),
          position: nextPosition++,
        });
      } else {
        shouldNotifyDuplicate = true;
      }
    });

    const newConfig = posConfig.config.map((page, index) => {
      if (index === currentPageIndex) {
        return Array.from(currentTiles.values());
      }
      return page;
    });

    setPosConfig({
      ...posConfig!,
      config: newConfig,
    });

    if (shouldNotifyDuplicate) {
      toast.success(t('duplicateTile'), {
        icon: (
          <Icon
            icon={icon({ name: 'exclamation-triangle', style: 'regular' })}
          />
        ),
      });
    }
  };

  const handleDeletePage = (pageIndex: number) => {
    setPosConfig({
      ...posConfig!,
      config: posConfig!.config.filter((_, index) => index !== pageIndex),
    });
    setSelectedPage(pageIndex > 0 ? pageIndex - 1 : 0);
  };

  const handleDeleteTile = (pageIndex: number, tileIndex: number) => {
    setPosConfig({
      ...posConfig!,
      config: posConfig!.config.map((page, index) =>
        index === pageIndex ? page.filter((_, i) => i !== tileIndex) : page
      ),
    });
  };

  const handleExpandPage = (pageIndex: number) => {
    setExpandedPage(
      expandedPage?.includes(pageIndex)
        ? expandedPage.filter((i) => i !== pageIndex)
        : [...(expandedPage || []), pageIndex]
    );
  };

  const isTileHovered = (pageIndex: number, tileIndex: number) => {
    return (
      hoveredTile?.pageIndex === pageIndex &&
      hoveredTile?.tileIndex === tileIndex
    );
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenDialog = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    setDialogOpen(true);
  };

  const handleOpenTemplateDialog = () => {
    setTemplateDialogOpen(true);
  };

  return (
    <Stack
      spacing={2}
      sx={{
        width: 400,
        height: 'calc(100vh - 64px)',
        bgcolor: 'background.paper',
        py: 2,
        overflow: 'auto',
      }}
    >
      <Stack sx={{ px: 2 }} spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body1" fontWeight={600}>
            {t('smartGridTemplates', {
              posType: posType === 'pos' ? 'POS' : 'Kiosk',
            })}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={handleOpenTemplateDialog}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon
                icon={icon({ name: 'plus-circle', style: 'regular' })}
                size="small"
              />
              {t('createTemplate')}
            </Stack>
          </Button>
        </Stack>

        {getTemplates().length > 0 && (
          <>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <SelectFilter
                field="posTemplate"
                label=""
                showDefaultOption={false}
                options={getTemplates()}
                value={posConfig?.id?.toString() || ''}
                setValue={(value) => {
                  const config = posConfigs?.find(
                    (config) => config.id.toString() === value?.toString()
                  );

                  if (config) {
                    setPendingTemplateConfig(config);
                    setConfirmTemplateChangeDialogOpen(true);
                  }
                }}
              />

              <DropdownMenu
                drawerOnMobile
                trigger={
                  <IconButton>
                    <Icon icon={icon({ name: 'ellipsis', style: 'regular' })} />
                  </IconButton>
                }
              >
                <Stack spacing={1} sx={{ p: 1 }}>
                  <ListItem
                    rounded
                    clickable
                    primary={t('editTemplate')}
                    icon={icon({
                      name: 'edit',
                      style: 'regular',
                    })}
                    onClick={() => {
                      setEditTemplateDialogOpen(true);
                    }}
                  />
                  <ListItem
                    rounded
                    clickable
                    primary={t('duplicateTemplate')}
                    icon={icon({
                      name: 'copy',
                      style: 'regular',
                    })}
                    onClick={() => {
                      handleDuplicateConfig(
                        posConfig?.id?.toString() || '',
                        (duplicatedConfig) => {
                          setPendingTemplateConfig(duplicatedConfig);
                          setConfirmDuplicateTemplateDialogOpen(true);
                        }
                      );
                    }}
                  />
                  <ListItem
                    rounded
                    clickable
                    primary={t('deleteTemplate')}
                    icon={icon({
                      name: 'trash-alt',
                      style: 'regular',
                    })}
                    onClick={() =>
                      setDeleteTemplateConfirmationDialogOpen(true)
                    }
                  />
                </Stack>
              </DropdownMenu>
            </Stack>

            <Stack spacing={1}>
              <DropdownMenu
                drawerOnMobile
                closeOnContentClick={false}
                trigger={
                  <Button
                    variant="text"
                    sx={{
                      color: 'text.primary',
                      p: 0,
                      width: '100%',
                    }}
                  >
                    <Stack
                      width="100%"
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ textDecoration: 'underline' }}
                      >
                        {t('manageLocations')}
                      </Typography>
                      <Tooltip title={t('tooltips.manageLocations')}>
                        <Icon
                          icon={icon({ name: 'info-circle', style: 'regular' })}
                          size="small"
                          sx={{ color: 'text.secondary' }}
                        />
                      </Tooltip>
                    </Stack>
                  </Button>
                }
              >
                {isLoadingLocations ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 4, pt: 1 }}
                  >
                    {t('loadingLocations')}
                  </Typography>
                ) : locations && locations.length > 0 ? (
                  <Stack spacing={0.5} sx={{ pl: 2, pt: 1 }}>
                    {locations.map((location) => (
                      <Stack
                        key={location.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          cursor: 'pointer',
                          p: 0.5,
                          borderRadius: 1,
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                        onClick={() => handleToggleLocation(location.id)}
                      >
                        <Checkbox
                          checked={selectedLocations.includes(location.id)}
                          onChange={() => handleToggleLocation(location.id)}
                          size="small"
                          sx={{
                            transition: 'all 0.2s ease',
                          }}
                        />
                        <Typography variant="body2">{location.name}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 4, pt: 1 }}
                  >
                    {t('noLocations')}
                  </Typography>
                )}
              </DropdownMenu>
            </Stack>
          </>
        )}
      </Stack>

      <Divider />
      {Array.isArray(posConfig?.config) && posConfig.config.length > 0 && (
        <>
          <Stack sx={{ px: 2 }} spacing={1}>
            {posConfig.config.map((page, index) => (
              <Stack key={index} spacing={1}>
                <Button
                  variant="text"
                  sx={{
                    color: 'text.primary',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 0,
                  }}
                  onClick={() => handleExpandPage(index)}
                  onMouseEnter={() => setHoveredPage(index)}
                  onMouseLeave={() => setHoveredPage(null)}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon
                      color="primary"
                      size="small"
                      icon={icon({ name: 'angle-up', style: 'regular' })}
                      sx={{
                        color: 'grey',
                        transform: expandedPage?.includes(index)
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {t('page', { pageNumber: index + 1 })}
                    </Typography>
                  </Stack>

                  <Button
                    variant="text"
                    onClick={() => handleDeletePage(index)}
                    sx={{
                      p: 0,
                      opacity: hoveredPage === index ? 1 : 0,
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  >
                    <Icon
                      icon={icon({ name: 'trash-alt', style: 'regular' })}
                      size="tiny"
                    />
                  </Button>
                </Button>

                <Collapse
                  in={expandedPage?.includes(index)}
                  timeout="auto"
                  unmountOnExit
                >
                  <Stack sx={{ pl: 2 }}>
                    {page
                      .slice()
                      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                      .map((item, tileIndex) => (
                        <Stack
                          key={item.name}
                          alignItems="center"
                          justifyContent="space-between"
                          direction="row"
                          onMouseEnter={() =>
                            setHoveredTile({ pageIndex: index, tileIndex })
                          }
                          onMouseLeave={() => setHoveredTile(null)}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Tooltip title={t(`tooltips.${item.itemType}`)}>
                              <Icon icon={getIcon(item.itemType)} />
                            </Tooltip>
                            <Typography variant="body2" fontWeight={400}>
                              {item.name}
                            </Typography>
                          </Stack>

                          <Button
                            variant="text"
                            sx={{
                              p: 0,
                              opacity: isTileHovered(index, tileIndex) ? 1 : 0,
                              transition: 'opacity 0.2s ease-in-out',
                              '&:hover': {
                                opacity: 1,
                              },
                            }}
                            onClick={() => handleDeleteTile(index, tileIndex)}
                          >
                            <Icon
                              icon={icon({
                                name: 'trash-alt',
                                style: 'regular',
                              })}
                              size="tiny"
                            />
                          </Button>
                        </Stack>
                      ))}
                    <Button
                      variant="text"
                      sx={{
                        justifyContent: 'start',
                        p: 1,
                        borderRadius: 1,
                        transition: 'background-color 0.2s ease',
                      }}
                      onClick={() => handleOpenDialog(index)}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon
                          color="primary"
                          size="small"
                          icon={icon({ name: 'plus-circle', style: 'regular' })}
                        />
                        <Typography variant="body2" fontWeight={400}>
                          {t('addTile', { pageNumber: index + 1 })}
                        </Typography>
                      </Stack>
                    </Button>
                  </Stack>
                </Collapse>
              </Stack>
            ))}
          </Stack>
          <Divider />
        </>
      )}

      {getTemplates().length > 0 && (
        <>
          <Stack sx={{ px: 2 }}>
            <Button
              variant="text"
              sx={{
                justifyContent: 'start',
                p: 0,
              }}
              onClick={handleAddTilePage}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon
                  size="small"
                  icon={icon({ name: 'plus-circle', style: 'regular' })}
                />
                <Typography variant="body2" fontWeight={400}>
                  {t('addTilePage')}
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </>
      )}

      <PosAddEditTemplateDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSave={(name) => {
          handleCreateConfig(name, (newConfig) => {
            setPendingTemplateConfig(newConfig);
            setConfirmNewTemplateDialogOpen(true);
          });
        }}
      />

      <PosAddEditTemplateDialog
        open={editTemplateDialogOpen}
        posName={posConfig?.name}
        onClose={() => setEditTemplateDialogOpen(false)}
        onSave={(name) => {
          if (posConfig) {
            handleSaveConfig({
              ...posConfig,
              name,
            });
          }
        }}
      />

      <PosConfigItemSelectorDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleAddTiles}
        type={posType}
      />

      <ConfirmationDialog
        open={deleteTemplateConfirmationDialogOpen}
        onClose={() => setDeleteTemplateConfirmationDialogOpen(false)}
        onConfirm={() => handleDeleteConfig(posConfig?.id?.toString() || '')}
        confirmText={t('deleteTemplate')}
        subtitle={t('deleteTemplateSubtitle')}
      />

      <ConfirmationDialog
        open={confirmTemplateChangeDialogOpen}
        onClose={() => setConfirmTemplateChangeDialogOpen(false)}
        onConfirm={() => {
          if (pendingTemplateConfig) {
            setPosConfig(pendingTemplateConfig);
          }
          setConfirmTemplateChangeDialogOpen(false);
        }}
        confirmText={t('changeTemplate')}
        subtitle={t('changeTemplateSubtitle')}
      />

      <ConfirmationDialog
        open={confirmNewTemplateDialogOpen}
        onClose={() => setConfirmNewTemplateDialogOpen(false)}
        onConfirm={() => {
          if (pendingTemplateConfig) {
            setPosConfig(pendingTemplateConfig);
          }
          setConfirmNewTemplateDialogOpen(false);
        }}
        confirmText={t('switchToNewTemplate')}
        subtitle={t('switchToNewTemplateSubtitle')}
      />

      <ConfirmationDialog
        open={confirmDuplicateTemplateDialogOpen}
        onClose={() => setConfirmDuplicateTemplateDialogOpen(false)}
        onConfirm={() => {
          if (pendingTemplateConfig) {
            setPosConfig(pendingTemplateConfig);
          }
          setConfirmDuplicateTemplateDialogOpen(false);
        }}
        confirmText={t('switchToDuplicatedTemplate')}
        subtitle={t('switchToDuplicatedTemplateSubtitle')}
      />
    </Stack>
  );
}
